import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../../../services/auth.service';

export default function Sidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [selectedBodega, setSelectedBodega] = useState<any>(null);
  const [isDispensacionOpen, setIsDispensacionOpen] = useState(false);

  useEffect(() => {
    setUser(authService.getCurrentUser());
    const storedBodega = localStorage.getItem('selectedBodega');
    if (storedBodega) {
      setSelectedBodega(JSON.parse(storedBodega));
    }

    // Expandir automáticamente el submenú si la ruta actual es de dispensación
    if (location.pathname.startsWith('/dispensacion')) {
      setIsDispensacionOpen(true);
    }

    // Auto-colapsar en pantallas pequeñas
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location.pathname]);

  const allMenuItems = [
    { path: '/dashboard', icon: 'ri-dashboard-line', label: 'Dashboard' },
    { path: '/catalogo-medicamentos', icon: 'ri-medicine-bottle-line', label: 'Catálogo' },
    { path: '/inventario', icon: 'ri-archive-line', label: 'Inventario' },
    { path: '/bodegas', icon: 'ri-building-line', label: 'Puntos Físicos' },
    { path: '/maestro-lotes', icon: 'ri-barcode-box-line', label: 'Maestro de Lotes' },
    { path: '/recepcion-tecnica', icon: 'ri-inbox-line', label: 'Recepción' },
    {
      isDropdown: true,
      label: 'Dispensación',
      icon: 'ri-hand-heart-line',
      subItems: [
        { path: '/dispensacion', label: 'Con formula externa', icon: 'ri-[#hand-heart-line]' },
        { path: '/dispensacion-formula', label: 'Con formula de Siima', icon: 'ri-[#file-paper-2-line]' }
      ]
    },
    { path: '/devoluciones', icon: 'ri-arrow-go-back-line', label: 'Devoluciones' },
    { path: '/compras', icon: 'ri-shopping-cart-line', label: 'Compras' },
    { path: '/traslados', icon: 'ri-truck-line', label: 'Traslados' },
    { path: '/clientes-proveedores', icon: 'ri-group-line', label: 'Clientes/Proveedores' },
    { path: '/gestion-especial', icon: 'ri-alert-line', label: 'Gestión Especial' },
    { path: '/reportes', icon: 'ri-file-chart-line', label: 'Reportes' },
    { path: '/usuarios', icon: 'ri-user-settings-line', label: 'Usuarios' },
    { path: '/pacientes', icon: 'ri-user-line', label: 'Pacientes' },
    { path: '/laboratorios', icon: 'ri-flask-line', label: 'Laboratorios' },
    { path: '/medicamentos-vigentes', icon: 'ri-shield-check-line', label: 'Med. Vigentes' }
  ];

  const getFilteredMenuItems = () => {
    if (selectedBodega) {
      if (selectedBodega.sw_ppal === true) {
        return allMenuItems;
      } else {
        const allowedPaths = [
          '/dashboard',
          '/catalogo-medicamentos',
          '/inventario',
          '/dispensacion',
          '/dispensacion-formula',
          '/devoluciones',
          '/compras',
          '/traslados',
          '/clientes-proveedores',
          '/reportes',
          '/pacientes',
          '/laboratorios'
        ];
        return allMenuItems.filter(item => {
          if (item.isDropdown) return true;
          return allowedPaths.includes(item.path);
        });
      }
    }

    if (user?.rol === 'Auxiliar de Farmacia' || user?.rol === 'Regente de Farmacia') {
      const allowedPaths = [
        '/dashboard',
        '/catalogo-medicamentos',
        '/inventario',
        '/recepcion-tecnica',
        '/dispensacion',
        '/dispensacion-formula',
        '/devoluciones',
        '/compras',
        '/traslados',
        '/clientes-proveedores',
        '/reportes',
        '/pacientes',
        '/laboratorios'
      ];
      return allMenuItems.filter(item => {
        if (item.isDropdown) return true;
        return allowedPaths.includes(item.path);
      });
    }
    return allMenuItems;
  };

  const menuItems = getFilteredMenuItems();

  return (
    <>
      <aside
        className={`fixed left-0 top-[73px] h-[calc(100vh-73px)] bg-white border-r border-slate-200 transition-all duration-300 z-40 ${isCollapsed ? 'w-20' : 'w-64'
          }`}
        id="main-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-200">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="w-full flex items-center justify-center p-2 text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
              title={isCollapsed ? 'Expandir menú' : 'Comprimir menú'}
            >
              <i className={`text-xl ${isCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`}></i>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {menuItems.map((item: any) => {
                if (item.isDropdown) {
                  const isAnySubActive = item.subItems.some((sub: any) => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'));

                  return (
                    <li key={item.label} className="space-y-1">
                      <button
                        onClick={() => setIsDispensacionOpen(!isDispensacionOpen)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all cursor-pointer group relative ${isAnySubActive
                            ? 'bg-teal-50/60 text-teal-700 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className="flex items-center gap-3">
                          <i className={`${item.icon} text-xl flex-shrink-0`}></i>
                          <span className={`text-sm font-medium transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                            {item.label}
                          </span>
                        </div>
                        {!isCollapsed && (
                          <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isDispensacionOpen ? 'rotate-180' : ''}`}></i>
                        )}
                      </button>

                      {/* Submenú Desplegable */}
                      {isDispensacionOpen && !isCollapsed && (
                        <ul className="pl-6 space-y-1">
                          {item.subItems.map((sub: any) => {
                            const isSubActive = location.pathname === sub.path || (sub.path !== '/dispensacion' && location.pathname.startsWith(sub.path));
                            return (
                              <li key={sub.path}>
                                <Link
                                  to={sub.path}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer ${isSubActive
                                      ? 'bg-teal-600 text-white font-semibold shadow-sm'
                                      : 'text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                                    }`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                  <span>{sub.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer group relative ${isActive
                        ? 'bg-teal-50 text-teal-600 font-semibold'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                      title={isCollapsed ? item.label : ''}
                    >
                      <i className={`${item.icon} text-xl flex-shrink-0`}></i>
                      <span className={`text-sm font-medium transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
                        }`}>
                        {item.label}
                      </span>

                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                          {item.label}
                          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800"></div>
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>

      <style>{`
        body {
          --sidebar-width: ${isCollapsed ? '80px' : '256px'};
        }
      `}</style>
    </>
  );
}
